import { useEffect, useState } from "react";
import { IconArrowLeft } from "@tabler/icons-react";
import { Button, Switch } from "@/components/ui";
import { useCatalogo, useRelaciones } from "../hooks/use-reporteria";
import { usePreviewInfinito } from "../hooks/use-preview";
import { esEjecutable, hidratarEtiquetas } from "../state/builder-ops";
import type { RepDefinicion as RepDef } from "../services/reporteria.api";
import { CatalogoTablas } from "./CatalogoTablas";
import { ColumnasSeleccionadas } from "./ColumnasSeleccionadas";
import { AjustesReporte } from "./AjustesReporte";
import { RelacionesYFormulas } from "./RelacionesYFormulas";
import { PreviewTabla } from "./PreviewTabla";
import { BuilderToolbar } from "./BuilderToolbar";

/**
 * ¿La definición usa funciones avanzadas (relaciones entre tablas, modo resumen,
 * columnas calculadas, fórmulas o transformaciones)? Un reporte que ya las use
 * debe abrir en modo avanzado para no esconder lo que trae.
 */
function usaFuncionesAvanzadas(d: RepDef): boolean {
  return (
    !!d.agrupar ||
    (d.joins?.length ?? 0) > 0 ||
    (d.columnasCalculadas?.length ?? 0) > 0 ||
    !!d.filtroFormula ||
    (d.tablas?.length ?? 0) > 1 ||
    d.columnas.some((c) => c.agregacion || c.expresion || c.mapaValores)
  );
}

/**
 * Builder libre (Fase 1/2). Se monta desde la galería con una definición inicial
 * y ofrece "Volver a reportes". Fase 2 · progresividad: por defecto arranca en
 * modo SIMPLE (columnas + filtros + orden + exportar); un switch revela las
 * "Opciones avanzadas" (relaciones entre tablas, modo resumen, columnas
 * calculadas, fórmulas y transformaciones). Un reporte que ya use algo avanzado
 * abre directamente en avanzado.
 */
export function ReporteriaBuilder({
  initialDef,
  onVolver,
}: {
  initialDef: RepDef;
  onVolver: () => void;
}) {
  const catalogo = useCatalogo();
  const relaciones = useRelaciones();
  const [def, setDefState] = useState<RepDef>(() => initialDef);
  const setDef = (f: (d: RepDef) => RepDef) => setDefState((prev) => f(prev));
  const [avanzado, setAvanzado] = useState(() => usaFuncionesAvanzadas(initialDef));
  const activo = esEjecutable(def);
  const datosPreview = usePreviewInfinito(def, activo);

  // Rellena las etiquetas legibles desde el catálogo cuando falten (p. ej. al
  // abrir un predefinido/plantilla guardado) · evita mostrar tokens opacos.
  // `hidratarEtiquetas` es idempotente (mismo objeto si nada cambia) → sin bucles.
  useEffect(() => {
    if (catalogo.data) setDefState((d) => hidratarEtiquetas(d, catalogo.data!));
  }, [catalogo.data, def]);

  // Si el reporte se vuelve avanzado (p. ej. el usuario agrega una 2ª tabla),
  // sube a avanzado para no ocultar controles que ya hacen falta (joins).
  useEffect(() => {
    if (!avanzado && usaFuncionesAvanzadas(def)) setAvanzado(true);
  }, [def, avanzado]);

  // Inalcanzable en la práctica: `ReporteriaBuilderPage` solo monta esta vista
  // con el catálogo ya cargado (misma query, caché compartida y observada por el
  // padre) y pinta su propio skeleton en la primera carga. Solo estrecha el tipo.
  if (!catalogo.data) return null;

  return (
    <div className="flex h-[calc(100dvh-4rem)] flex-col gap-3 p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onVolver}>
          Volver a reportes
        </Button>
        <Switch
          checked={avanzado}
          onCheckedChange={setAvanzado}
          label="Opciones avanzadas"
        />
      </div>
      <BuilderToolbar def={def} setDef={setDef} />

      <div className="grid min-h-0 flex-1 gap-3 lg:grid-cols-[18rem_minmax(0,1fr)_minmax(0,1fr)]">
        <div className="min-h-0 lg:h-full">
          <CatalogoTablas catalogo={catalogo.data} def={def} setDef={setDef} />
        </div>

        <div className="min-h-0 space-y-3 overflow-y-auto lg:h-full">
          <ColumnasSeleccionadas def={def} setDef={setDef} avanzado={avanzado} />
          <AjustesReporte def={def} setDef={setDef} catalogo={catalogo.data} avanzado={avanzado} />
          {avanzado && (
            <RelacionesYFormulas
              def={def}
              setDef={setDef}
              catalogo={catalogo.data}
              relaciones={relaciones.data ?? []}
            />
          )}
        </div>

        <div className="min-h-0 lg:h-full">
          <PreviewTabla activo={activo} datos={datosPreview} def={def} setDef={setDef} />
        </div>
      </div>
    </div>
  );
}
